/*
 * Shared kit for the production vector art ("premium casual"): soft volume — 3–4 stop gradients, a highlight
 * top-left, a soft shadow below, neat outlines; one light source top-left for every object.
 * Palette: strictly docs/lovable-graphics-prompt.md (dark/light accents) + tints/shades of them.
 * Every <defs> id is prefixed (`p`) so several copies of the same art can live on one page.
 */
export type ArtTheme = 'dark' | 'light';

export interface ArtPal {
  teal: string;
  orange: string;
  gold: string;
  red: string;
  blue: string;
  pink: string;
  purple: string;
  green: string;
  sky: string;
  ink: string;
  /** UI background / panel (for scene backgrounds) */
  bg: string;
  panel: string;
  /** roulette "black" cell — dark slate (theme token --roulette-black) */
  slate: string;
  dark: boolean;
}

export const ART_PAL: Record<ArtTheme, ArtPal> = {
  dark: {
    teal: '#2EE6C9',
    orange: '#FF8A3D',
    gold: '#FFD166',
    red: '#E5484D',
    blue: '#5B7CFA',
    pink: '#F07AA8',
    purple: '#A78BFA',
    green: '#7BE07B',
    sky: '#4FC3F7',
    ink: '#E4EAF4',
    bg: '#0A0F1A',
    panel: '#0E1522',
    slate: '#1E2A3F',
    dark: true,
  },
  light: {
    teal: '#0B8A76',
    orange: '#D9650B',
    gold: '#B7791F',
    red: '#D6363C',
    blue: '#3B5BDB',
    pink: '#D6457E',
    purple: '#7C5CE0',
    green: '#2F9E44',
    sky: '#1C7ED6',
    ink: '#111827',
    bg: '#F4F6FA',
    panel: '#FFFFFF',
    slate: '#2B3445',
    dark: false,
  },
};

/* ---------------- colour math (tints / shades of the palette) ---------------- */

const hex2 = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');

/** Linear mix of two #RRGGBB colours, t = 0 → a, 1 → b. */
export function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return '#' + pa.map((v, i) => hex2(v + ((pb[i] ?? 0) - v) * t)).join('').toUpperCase();
}
export const tint = (c: string, t: number) => mix(c, '#FFFFFF', t);
export const shade = (c: string, t: number) => mix(c, '#000000', t);

/* ---------------- number formatting (keeps the SVG small) ---------------- */

export const n = (v: number) => (Math.round(v * 10) / 10).toString();

/* ---------------- gradients ---------------- */

/**
 * Sphere-like volume: radial, light spot top-left (4 stops: highlight, base, shade, deep shade).
 * Use for round objects (planets, fruit, bubbles, hub).
 */
export function ball(id: string, c: string, hi = 0.45, lo = 0.35): string {
  return (
    `<radialGradient id="${id}" cx=".36" cy=".3" r=".78" fx=".3" fy=".24">` +
    `<stop offset="0" stop-color="${tint(c, hi)}"/><stop offset=".38" stop-color="${c}"/>` +
    `<stop offset=".8" stop-color="${shade(c, lo * 0.55)}"/><stop offset="1" stop-color="${shade(c, lo)}"/></radialGradient>`
  );
}

/** Body volume: linear top-left → bottom-right, 4 stops. `a` = angle hint via x2/y2. */
export function body(id: string, c: string, hi = 0.4, lo = 0.32, x2 = 0.55, y2 = 1): string {
  return (
    `<linearGradient id="${id}" x1=".15" y1="0" x2="${x2}" y2="${y2}">` +
    `<stop offset="0" stop-color="${tint(c, hi)}"/><stop offset=".35" stop-color="${c}"/>` +
    `<stop offset=".75" stop-color="${shade(c, lo * 0.5)}"/><stop offset="1" stop-color="${shade(c, lo)}"/></linearGradient>`
  );
}

/** Vertical scene gradient (3 stops). */
export function vgrad(id: string, top: string, mid: string, bottom: string): string {
  return (
    `<linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${top}"/><stop offset=".55" stop-color="${mid}"/><stop offset="1" stop-color="${bottom}"/></linearGradient>`
  );
}

/** Soft radial glow/fade used for ground shadows and light pools. */
export function fade(id: string, c: string, a = 0.5): string {
  return (
    `<radialGradient id="${id}"><stop offset="0" stop-color="${c}" stop-opacity="${a}"/>` +
    `<stop offset=".6" stop-color="${c}" stop-opacity="${n(a * 0.35)}"/><stop offset="1" stop-color="${c}" stop-opacity="0"/></radialGradient>`
  );
}

/** Soft ground shadow (needs a `fade` gradient with the shadow colour). */
export const shadow = (fid: string, cx: number, cy: number, rx: number, ry: number) =>
  `<ellipse cx="${n(cx)}" cy="${n(cy)}" rx="${n(rx)}" ry="${n(ry)}" fill="url(#${fid})"/>`;

/** Top-left highlight (the blick) — a rotated translucent white ellipse. */
export const gloss = (cx: number, cy: number, rx: number, ry: number, rot = -35, o = 0.55) =>
  `<ellipse cx="${n(cx)}" cy="${n(cy)}" rx="${n(rx)}" ry="${n(ry)}" transform="rotate(${rot} ${n(cx)} ${n(cy)})" fill="#FFFFFF" fill-opacity="${o}"/>`;

/** Outline colour for an object of colour `c` (darker, soft). */
export const edge = (c: string, P: ArtPal) => (P.dark ? shade(c, 0.45) : shade(c, 0.3));

/** Sunny gold: the light-theme gold (#B7791F) reads brownish on big shapes, so it is tinted there. */
export const warm = (P: ArtPal) => (P.dark ? P.gold : tint(P.gold, 0.3));

/** Shadow colour of the scene. */
export const shadowInk = (P: ArtPal) => (P.dark ? '#000000' : shade(P.ink, 0.2));

/* ---------------- geometry ---------------- */

/** Star/polygon points (R outer, r inner, k tips). */
export function star(cx: number, cy: number, R: number, r: number, k: number, rot = -Math.PI / 2): string {
  const p: string[] = [];
  for (let i = 0; i < k * 2; i++) {
    const a = rot + (i * Math.PI) / k;
    const rr = i % 2 ? r : R;
    p.push(n(cx + rr * Math.cos(a)) + ',' + n(cy + rr * Math.sin(a)));
  }
  return p.join(' ');
}

/** Gear outline path with rounded-looking trapezoid teeth. */
export function gearPath(cx: number, cy: number, R: number, r: number, teeth: number): string {
  const seg = (Math.PI * 2) / teeth;
  let d = '';
  for (let i = 0; i < teeth; i++) {
    const a = i * seg;
    const pts = [
      [r, a - seg * 0.5],
      [r, a - seg * 0.3],
      [R, a - seg * 0.18],
      [R, a + seg * 0.18],
      [r, a + seg * 0.3],
    ] as const;
    pts.forEach(([rad, ang], j) => {
      d += (i === 0 && j === 0 ? 'M' : 'L') + n(cx + rad * Math.cos(ang)) + ' ' + n(cy + rad * Math.sin(ang));
    });
  }
  return d + 'Z';
}

/** Annular sector (wheel slice) path. */
export function sector(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): string {
  const p = (r: number, a: number) => n(cx + r * Math.cos(a)) + ' ' + n(cy + r * Math.sin(a));
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M${p(r0, a0)}L${p(r1, a0)}A${r1} ${r1} 0 ${large} 1 ${p(r1, a1)}L${p(r0, a1)}A${r0} ${r0} 0 ${large} 0 ${p(r0, a0)}Z`;
}

/** Deterministic pseudo-random (for star fields, sprinkles) — same art on every render. */
export function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/** Wrap a scene into a standalone SVG document. */
export function svgDoc(w: number, h: number, inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">${inner}</svg>`;
}

/**
 * Standard cover background: vertical scene gradient + a calm left third (labels sit there) +
 * a soft light pool behind the focus object (right half).
 */
export function coverBg(
  p: string,
  P: ArtPal,
  top: string,
  mid: string,
  bottom: string,
  glowAt: [number, number] = [440, 190],
  w = 640,
  h = 400,
): { defs: string; body: string } {
  const defs =
    vgrad(`${p}bg`, top, mid, bottom) +
    fade(`${p}pool`, P.dark ? tint(mid, 0.25) : '#FFFFFF', P.dark ? 0.35 : 0.8) +
    `<linearGradient id="${p}lft" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${bottom}" stop-opacity="${P.dark ? 0.55 : 0.35}"/><stop offset="1" stop-color="${bottom}" stop-opacity="0"/></linearGradient>`;
  const body =
    `<rect width="${w}" height="${h}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${glowAt[0]}" cy="${glowAt[1]}" rx="${n(h * 0.62)}" ry="${n(h * 0.52)}" fill="url(#${p}pool)"/>` +
    `<rect width="${n(w * 0.38)}" height="${h}" fill="url(#${p}lft)"/>`;
  return { defs, body };
}
